
import { FC, useState, useMemo } from 'react'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import { useUser } from '@clerk/clerk-react'
import { toast } from 'sonner'
import './wallet-explainer.css'

// COMPONENTS
import * as Icons from '@icons'
import { Column } from '@components/templates/column'
import Button from '@components/atoms/button'
import Card from '@/components/atoms/card'
import StepTracker from '@/components/organisims/step-tracker'
import MyCardDetails from '@/components/organisims/my-card-details'

// TYPES
import type { RouteObject } from 'react-router-dom'

// UTILITIES
import { parseTeraUserData } from '@schemas'
import { createAuthenticatedLoader } from '@utils/loaders'


interface Step {
    title: string
    description: string
    image: string
}

const steps: Step[] = [
    {
        title: "Open Apple Wallet",
        description: "Open the Wallet app on your iPhone and tap the '+' button in the top right corner.",
        image: "/apple-pay/step-1.png"
    },
    {
        title: "Add To Wallet",
        description: "Scroll down and tap Debit or Credit Card in your wallet.",
        image: "/apple-pay/step-2.png"
    },
    {
        title: "Debit Or Credit Card",
        description: "Tap Continue to begin adding your Splitcard details manually or by pasting copied number.",
        image: "/apple-pay/step-3.png"
    },
    {
        title: "Add Card",
        description: "Tap 'Enter Card Detail Manually' at the bottom of the screen to paste your virtual card number.",
        image: "/apple-pay/step-4.png"
    },
    {
        title: "Card Details",
        description: "Tap reveal and copy to capture card number. Paste into Card Number field.",
        image: "/apple-pay/step-5.png"
    }
]

const WalletExplainerScreen: FC<{}> = () => {
    const navigate = useNavigate()
    const { user } = useUser()
    const [currentStep, setCurrentStep] = useState(1)

    const location = useLocation()
    const isPhysical = location.state?.isPhysical
    const singleUseCardId = location.state?.cardId as string | undefined
    const singleUseLastFour = location.state?.lastFour as string | undefined
    const returnUrl = location.state?.returnUrl as string | undefined

    const { virtualCard, physicalCard } = useMemo(() => {
        return parseTeraUserData(user?.publicMetadata.tera)
    }, [user?.publicMetadata.tera])

    const card = isPhysical ? physicalCard : virtualCard
    const isSingleUse = !!singleUseCardId

    const handleBack = () => {
        if (currentStep > 1) setCurrentStep(prev => prev - 1)
        else navigate(-1)
    }

    const handleNext = () => {
        if (currentStep < 5) setCurrentStep(prev => prev + 1)
        else navigate(returnUrl ?? '/account')
    }

    const currentStepData = steps[currentStep - 1]

    return (
        <Column className="wallet-explainer-stack">
            <Column.Content className="wallet-explainer-screen">
                <Column.Header.Start>
                    <div className='flex flex-row gap-2 items-center' style={{ color: '#4F39F6' }}>
                        <Icons.Wallet tint={'#4F39F6'} width="1.25rem" height="1.25rem" />
                        <h5><strong>Add to Apple Wallet</strong></h5>
                    </div>
                </Column.Header.Start>

                <Column.Header.End>
                    <Link className="account-redirect-container" to="/account">
                        <Icons.House tint="var(--splitcard-black)" />
                    </Link>
                </Column.Header.End>

                <Card style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }} light={true}>
                    <div>
                        <h3 className='!mb-2'>{currentStepData.title}</h3>
                        <p className='text-body'>{currentStepData.description}</p>
                    </div>
                </Card>

                {currentStep === 5 && (<>
                    <p className='text-body text-gray-500' style={{ margin: '1rem 0' }}>After pasting the card number, you must update the expiration date.</p>
                    <MyCardDetails
                        cardId={singleUseCardId ?? card?.id}
                        cardHolder={user}
                        lastFour={isSingleUse ? singleUseLastFour : card?.lastFour}
                        physical={isPhysical}
                        reloadable={!isPhysical && !isSingleUse}
                        handleCopy={() => toast.success('Card number copied')}
                        style={{ marginTop: '0.5rem' }}
                    />
                
                </>
                )}

                <div style={{ display: 'flex', justifyContent: 'center', marginTop: '1rem' }}>
                    <img src={currentStepData.image} alt={`Step ${currentStep}`} style={{ maxWidth: '85%' }} />
                </div>

                <Column.Footer>
                    <div className='flex flex-col gap-2'>
                        <div className='flex flex-row gap-2'>
                            <Button
                                className='flex flex-row flex-wrap items-center justify-start gap-4'
                                style={{ whiteSpace: 'nowrap', flex: 1 }}
                                theme="light"
                                onClick={handleBack}
                            >
                                <Icons.Chevron className='!ml-4' tint={'var(--splitcard-black)'} width="1.25rem" height="1.25rem" facing="left" />
                                Back
                            </Button>
                            <Button
                                className='flex flex-row flex-wrap items-center justify-end gap-4'
                                style={{ whiteSpace: 'nowrap', flex: 1 }}
                                theme="primary"
                                onClick={handleNext}
                            >
                                {currentStep === 5 ? 'Finish' : 'Next Step'}
                                <Icons.Chevron className='!mr-4' tint={'var(--splitcard-white)'} width="1.25rem" height="1.25rem" facing="right" />
                            </Button>
                        </div>
                        <StepTracker currentStep={currentStep} steps={5} withMarginTop={false} />
                    </div>
                </Column.Footer>
            </Column.Content>
        </Column>
    )
}

const loader = createAuthenticatedLoader(() => null)

const WalletExplainerRoute: RouteObject = {
    path: "wallet-explainer",
    element: <WalletExplainerScreen />,
    loader,
}

export default WalletExplainerRoute
